import { Link } from "react-router-dom";
import Seo, { orgSchema, breadcrumbSchema } from "@/components/Seo";
import PageHeader from "@/components/PageHeader";
import { FadeUp, Eyebrow, MaskedLines, ArrowLink } from "@/components/Reveal";
import { Accordion } from "@/components/Accordion";
import { FINANCING, SITE } from "@/data/content";

export default function Financing() {
    return (
        <>
            <Seo
                title="Financing up to $10M — Construction Loans | Dagaz Development"
                description="Construction loans, construction-to-permanent structures and land-as-down-payment — plus organized project financing for qualifying developer builds up to $10 million USD, across the US and Canada."
                path="/financing"
                jsonLd={[
                    orgSchema,
                    breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Financing", path: "/financing" }]),
                    {
                        "@context": "https://schema.org",
                        "@type": "Service",
                        name: "Construction and Project Financing",
                        provider: { "@type": "Organization", name: SITE.name, email: SITE.email },
                        areaServed: ["United States", "Canada"],
                        description: "Construction loans, construction-to-permanent loans, land-as-down-payment structures and organized developer project financing up to $10 million USD through partner lenders.",
                    },
                    {
                        "@context": "https://schema.org",
                        "@type": "FAQPage",
                        mainEntity: FINANCING.faqs.map((f) => ({
                            "@type": "Question",
                            name: f.q,
                            acceptedAnswer: { "@type": "Answer", text: f.a },
                        })),
                    },
                ]}
            />
            <PageHeader
                eyebrow="Financing — Up to $10M USD"
                titleLines={["Financing that", "fits the build."]}
                answer="Dagaz Development arranges financing with partner lenders across the US and Canada — construction loans, construction-to-permanent structures and land-as-down-payment — plus organized project financing for qualifying developer builds up to $10 million USD. Lending decisions always rest with the lender."
                crumbs={[{ name: "Financing" }]}
            />

            <section className="px-6 md:px-12 lg:px-16 py-24 md:py-32 bg-chalk" data-testid="financing-types">
                <div className="grid lg:grid-cols-12 gap-14">
                    <div className="lg:col-span-4">
                        <FadeUp>
                            <p className="font-serif font-light text-7xl md:text-9xl text-graphite leading-none">$10M</p>
                            <p className="mt-4 font-sans text-[10px] font-medium tracking-[0.24em] uppercase text-slate max-w-[220px]">
                                Ceiling for qualifying developer & commercial projects
                            </p>
                        </FadeUp>
                    </div>
                    <div className="lg:col-span-8 grid sm:grid-cols-2 gap-px bg-graphite/10 border border-graphite/10">
                        {FINANCING.types.map((t, i) => (
                            <FadeUp key={t.name} delay={0.08 * i} className="bg-chalk p-8 md:p-10">
                                <p className="font-sans text-[10px] font-medium tracking-[0.28em] text-gold">{String(i + 1).padStart(2, "0")}</p>
                                <h2 className="mt-4 font-serif font-light text-2xl md:text-3xl text-graphite">{t.name}</h2>
                                <p className="mt-4 font-sans text-sm leading-relaxed text-slate">{t.body}</p>
                            </FadeUp>
                        ))}
                    </div>
                </div>
            </section>

            <section className="bg-graphite bg-noise px-6 md:px-12 lg:px-16 py-28 md:py-36" data-testid="financing-process">
                <Eyebrow dark className="mb-8">How It Unfolds</Eyebrow>
                <MaskedLines
                    lines={["Four steps between", "you and the funds."]}
                    inView
                    className="font-serif font-light text-4xl md:text-6xl leading-[1.02] tracking-tight text-chalk"
                />
                <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-12 border-t border-chalk/15 pt-12">
                    {FINANCING.steps.map((s, i) => (
                        <FadeUp key={s.title} delay={0.08 * i}>
                            <p className="font-serif font-light text-5xl text-chalk/10">{String(i + 1).padStart(2, "0")}</p>
                            <h3 className="mt-4 font-serif text-2xl text-chalk">{s.title}</h3>
                            <p className="mt-3 font-sans text-sm leading-relaxed text-fog">{s.body}</p>
                        </FadeUp>
                    ))}
                </div>
            </section>

            <section className="px-6 md:px-12 lg:px-16 py-24 md:py-36 bg-chalk grid lg:grid-cols-12 gap-14" data-testid="financing-developer">
                <div className="lg:col-span-6">
                    <Eyebrow className="mb-8">Developer & Investor Projects</Eyebrow>
                    <MaskedLines
                        lines={["Organized, financed,", "delivered."]}
                        inView
                        className="font-serif font-light text-4xl md:text-5xl leading-[1.05] tracking-tight text-graphite"
                    />
                    <FadeUp delay={0.2} className="mt-8 space-y-6 font-sans text-base leading-relaxed text-graphite/80">
                        <p>
                            For developers and landowners we organize the whole venture: land feasibility, entitlement strategy, design, delivery management — and the financing structure behind it, with facilities up to $10 million USD for qualifying projects through partner lenders.
                        </p>
                        <p>
                            We cooperate directly with real estate agents and brokers on both ends of a project: land acquisition at the start, and marketing and sales of completed homes at the finish. One table, one set of numbers, everyone informed.
                        </p>
                    </FadeUp>
                    <FadeUp delay={0.3} className="mt-10">
                        <ArrowLink to="/partners" testid="financing-partners-link">Cooperation for Lenders & Agents</ArrowLink>
                    </FadeUp>
                </div>
                <div className="lg:col-span-5 lg:col-start-8">
                    <FadeUp delay={0.15} className="border border-graphite/15 p-8 md:p-10 bg-fog/30">
                        <p className="font-sans text-[10px] font-medium tracking-[0.28em] uppercase text-slate mb-6">What lenders receive from us</p>
                        <ul className="space-y-4">
                            {FINANCING.lenderPackage.map((item) => (
                                <li key={item} className="flex items-baseline gap-4 font-sans text-sm text-graphite/80">
                                    <span className="w-6 h-px bg-gold shrink-0 translate-y-[-4px]" aria-hidden="true" />
                                    {item}
                                </li>
                            ))}
                        </ul>
                    </FadeUp>
                </div>
            </section>

            <section className="px-6 md:px-12 lg:px-16 pb-28 md:pb-40 bg-chalk" data-testid="financing-faq">
                <div className="grid lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-3">
                        <Eyebrow className="mb-6">Financing Questions</Eyebrow>
                    </div>
                    <div className="lg:col-span-8 lg:col-start-5">
                        <Accordion items={FINANCING.faqs} testidPrefix="financing-faq" />
                        <FadeUp className="mt-12">
                            <Link
                                to="/start-a-project"
                                data-testid="financing-cta-start-project"
                                className="group inline-flex items-center gap-3 px-8 py-4 bg-forest text-chalk font-sans text-xs font-semibold tracking-[0.22em] uppercase transition-colors duration-500 hover:bg-graphite"
                            >
                                Start a Project
                                <span className="inline-block transition-transform duration-500 group-hover:translate-x-1.5" aria-hidden="true">→</span>
                            </Link>
                        </FadeUp>
                    </div>
                </div>
            </section>
        </>
    );
}
